import React, { useState } from 'react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  ClientExclusion,
  ClientExclusionType,
  useCreateClientExclusionMutation,
  useDeleteClientExclusionMutation,
} from 'src/pages/client-management/endpoints/client-exclusions.endpoints';

interface ExclusionListSectionProps {
  title: string;
  type: ClientExclusionType;
  entries: ClientExclusion[];
  isLoading: boolean;
}

const ExclusionListSection: React.FC<ExclusionListSectionProps> = ({
  title,
  type,
  entries,
  isLoading,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [pendingRemove, setPendingRemove] = useState<ClientExclusion | null>(null);
  const [createExclusion, { isLoading: isCreating }] = useCreateClientExclusionMutation();
  const [deleteExclusion, { isLoading: isDeleting }] = useDeleteClientExclusionMutation();

  const handleAdd = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    try {
      await createExclusion({ type, value: trimmed }).unwrap();
      setInputValue('');
    } catch {
      // RTK global error handling surfaces API failures.
    }
  };

  const handleConfirmRemove = async () => {
    if (!pendingRemove) return;

    try {
      await deleteExclusion(pendingRemove.id).unwrap();
      setPendingRemove(null);
    } catch {
      // RTK global error handling surfaces API failures.
    }
  };

  return (
    <section className="rounded-lg border border-gray-400 bg-white p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">{title}</h2>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center mb-4">
        <Input
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          placeholder={`Add ${type}`}
          className="sm:flex-1"
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void handleAdd();
            }
          }}
        />
        <Button
          variant="black"
          size="med"
          onClick={() => void handleAdd()}
          disabled={!inputValue.trim() || isCreating}
        >
          Add
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading exclusions…</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-gray-500">No exclusions configured.</p>
      ) : (
        <ul className="divide-y divide-gray-300 border border-gray-300 rounded-md">
          {entries.map((entry) => (
            <li
              key={entry.id}
              className="flex items-center justify-between gap-4 px-4 py-3"
            >
              <span className="text-sm text-gray-900 break-all">{entry.value}</span>
              <Button
                variant="delete"
                size="sm"
                onClick={() => setPendingRemove(entry)}
                disabled={isDeleting}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={pendingRemove !== null} onOpenChange={(open) => !open && setPendingRemove(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove exclusion?</DialogTitle>
            <DialogDescription>
              {pendingRemove
                ? `Remove "${pendingRemove.value}" from excluded ${type}s? Client reports will include this ${type} again immediately.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPendingRemove(null)}>
              Cancel
            </Button>
            <Button variant="delete" onClick={() => void handleConfirmRemove()} disabled={isDeleting}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ExclusionListSection;
